/**
 * T56 Fix: Sketch utility functions
 *
 * Provides functions to restore and clear sketch data from sessionStorage
 * for both action and accident sketches.
 */

import Debug from 'debug';
import { formActionNaming } from '../components/forms/ActionForms/constants';
import { formAccidentNaming } from '../components/forms/AddBasicInfoAccidentForm/constants';
import { client } from '../gql/client';
import { GET_IMAGE_BY_ID } from '../gql/queries/accidents/accidents';
import { resolveImageUrl } from './cloudinary';

const log = Debug('rois:sketch:utils');
const enableSketchDebug = () => Debug.enable('rois:sketch:*');

type SketchType = 'action' | 'accident';

/**
 * Converts a Blob to a data URI string
 */
const blobToDataUri = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Gets the naming constants based on sketch type
 */
const getNaming = (type: SketchType) => {
  return type === 'action' ? formActionNaming : formAccidentNaming;
};

/**
 * Restores sketch data from the server to sessionStorage
 * This allows the Fabric.js canvas to load existing sketch data when editing
 *
 * @param imageId - The ID of the image/sketch in the database
 * @param type - Whether this is an 'action' or 'accident' sketch
 * @returns true if restoration was successful, false otherwise
 */
export const restoreSketchToSessionStorage = async (
  imageId: string,
  type: SketchType
): Promise<boolean> => {
  const naming = getNaming(type);

  try {
    enableSketchDebug();
    log('🔄 Restoring sketch %s for %s', imageId, type);

    // 1. Query the server for sketch data including SVG and FabricJSON
    const { data } = await client.query({
      query: GET_IMAGE_BY_ID,
      variables: { id: imageId },
      fetchPolicy: 'network-only', // Always fetch fresh data
    });

    const image = data?.image;
    if (!image) {
      log('⚠️ Image not found: %s', imageId);
      return false;
    }

    log('📋 Image data: %O', {
      id: image.id,
      name: image.name,
      hasSketchSvg: !!image.sketchSvg,
      hasSketchFabricJson: !!image.sketchFabricJson,
      cloudinaryPublicId: image.cloudinaryPublicId,
    });

    // 2. If we have SVG and FabricJSON, use them directly for full editability
    if (image.sketchSvg && image.sketchFabricJson) {
      log('🎨 Using SVG and FabricJSON from server');

      sessionStorage.setItem(naming.SVG_CANVAS, image.sketchSvg);
      sessionStorage.setItem(naming.ARRAY_CANVAS, image.sketchFabricJson);

      // Also fetch the PNG for preview (backend-resolved URL; legacy fallback inside)
      try {
        const imageUrl = resolveImageUrl(image, 'full');
        if (imageUrl) {
          const response = await fetch(imageUrl, { mode: 'cors' });
          if (response.ok) {
            const blob = await response.blob();
            const dataUri = await blobToDataUri(blob);
            sessionStorage.setItem(naming.IMAGE_CANVAS, dataUri);
          }
        }
      } catch (e) {
        log('⚠️ Could not fetch PNG preview: %O', e);
        // Continue without PNG - SVG/JSON are more important
      }

      log('✅ Successfully restored sketch with full editability');
      return true;
    }

    // 3. Fallback: If only PNG exists (old sketches), load as background image
    // User can draw over it but cannot edit existing objects
    log('🖼️ Using PNG fallback (old sketch format)');

    const imageUrl = resolveImageUrl(image, 'full');
    if (!imageUrl) {
      log('❌ No resolvable URL for image %s', imageId);
      return false;
    }

    const response = await fetch(imageUrl, { mode: 'cors' });
    if (!response.ok) {
      log('❌ Failed to fetch PNG: %d', response.status);
      return false;
    }

    const blob = await response.blob();
    const dataUri = await blobToDataUri(blob);

    sessionStorage.setItem(naming.IMAGE_CANVAS, dataUri);
    sessionStorage.setItem(naming.SVG_CANVAS, '');
    sessionStorage.setItem(naming.ARRAY_CANVAS, '[]');

    log('✅ Successfully restored sketch (PNG only mode)');
    return true;
  } catch (error) {
    log('❌ Error restoring sketch: %O', error);
    return false;
  }
};

/**
 * Clears sketch data from sessionStorage
 * Use this when creating a new sketch or closing the modal without saving
 *
 * @param type - Whether this is an 'action' or 'accident' sketch
 */
export const clearSketchFromSessionStorage = (type: SketchType): void => {
  enableSketchDebug();
  const naming = getNaming(type);

  sessionStorage.removeItem(naming.IMAGE_CANVAS);
  sessionStorage.removeItem(naming.SVG_CANVAS);
  sessionStorage.removeItem(naming.ARRAY_CANVAS);

  log('🧹 Cleared sketch data for %s', type);
};

/**
 * Checks if there is sketch data in sessionStorage
 *
 * @param type - Whether this is an 'action' or 'accident' sketch
 * @returns true if sketch data exists
 */
export const hasSketchInSessionStorage = (type: SketchType): boolean => {
  const naming = getNaming(type);

  const imageCanvas = sessionStorage.getItem(naming.IMAGE_CANVAS);
  const svgCanvas = sessionStorage.getItem(naming.SVG_CANVAS);
  const arrayCanvas = sessionStorage.getItem(naming.ARRAY_CANVAS);

  return !!(imageCanvas || svgCanvas || (arrayCanvas && arrayCanvas !== '[]'));
};

/**
 * Gets the current sketch data from sessionStorage
 * Used when saving the sketch to the server
 *
 * @param type - Whether this is an 'action' or 'accident' sketch
 * @returns Object with imageDataUri, svgContent, and fabricJson
 */
export const getSketchFromSessionStorage = (
  type: SketchType,
  // El reintento de la cola pasa su almacén en memoria: no debe leer el croquis
  // del formulario abierto (spec outbox-autocontenido v2 § 5.1).
  store: Pick<Storage, 'getItem'> = sessionStorage,
): {
  imageDataUri: string | null;
  svgContent: string | null;
  fabricJson: string | null;
} => {
  const naming = getNaming(type);

  return {
    imageDataUri: store.getItem(naming.IMAGE_CANVAS),
    svgContent: store.getItem(naming.SVG_CANVAS),
    fabricJson: store.getItem(naming.ARRAY_CANVAS),
  };
};
